import React, { useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import Lightbox from 'yet-another-react-lightbox';
import Video from 'yet-another-react-lightbox/dist/plugins/video';
import Zoom from 'yet-another-react-lightbox/dist/plugins/zoom';
import 'yet-another-react-lightbox/dist/styles.css';
import {
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem
} from 'reactstrap';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import _ from 'lodash';

// constant
import { CDN_URL } from '../../constants/constant';
import LocalStrings from '../../assets/languages';
import { showError } from './alertmessage';
import {
  getAppTabId,
  getConditionalCommonFeatures,
  getUserRolePrivilege,
  handleTrickyBoolean,
  renderProfileSrc
} from '../../util/util-common';
import { ALLOWED_FEATURE_KEYS } from '../../constants/section-constants/common-constants';

//actions
import {
  commonNetworkModal,
  setProfilePortalToggle,
  setShowCommonAccessDenied
} from '../../actions';
import { fetchAppUserNotes, clearAppUserNotes } from '../notes/actions';
import {
  setIsVSPopup,
  setVSPopupContent
} from './vertical-sliding-popup/actions';
import { setHSPopup } from './horizontal-sliding-popup/actions';
import { showAttendeeProfile } from '../attendee/actions';
import { openMeetingModal } from '../meetings/actions';

//files & components
import NoImgIcon from '../../assets/img/placeholders/NoImg.svg';

/**
 * @param info JSON | User information
 * @param size (optional) Number | Desired dimension for the profile image to render
 * @param disable (optional) Boolean | Disables the profile redirections
 * @param dontCheckNW (optional) Boolean | For profiles where NW checkiing not necessary
 * @param openProfile (optional) Boolean | For redirecting to profile details, default true
 * @param showOptions  (optional) Boolean | Default false, for showing photo view and video view options
 * @param hideVideo (optional) Boolean | Default false, hides the video ring
 * @param hideFrame (optional) Boolean | Default false, hides the users frame
 * @param hasHSPopup (optional) Boolean | Default false, required to determine to use HSPopup related actions
 * @param isPostComment (optional) Boolean | Default false, denotes if called form post-comment
 * @param source (options) string/enum | Source of showAttendeeProfile supported redirection
 */
const UserProfilePic = props => {
  const {
    // state
    session,
    userDetails,
    userDetailsAddOn,
    rolePermissions,
    appTabId,
    checkNotes = true,

    // component
    info,
    size = 40,
    disable = false,
    dontCheckNW = false,
    openProfile = true,
    showOptions = false,
    hideVideo = false,
    hideFrame = false,
    hasHSPopup = false,
    isPostComment = false,
    forceRedirect = false,
    showVideo = false,
    source = ALLOWED_FEATURE_KEYS.attendee,
    forwardedRef
  } = props;

  const history = useHistory();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [toggle, setToggle] = useState(false);
  const [view, setView] = useState();

  //for triggering profileRedirection from parent
  const childRef = useRef();
  const ref = forwardedRef || childRef;

  let isNetworking = true;
  let isInvisible = false;
  (() => {
    if (!_.isEmpty(info)) {
      if (Number(info?.appuserid) !== Number(session?.userid)) {
        isInvisible = handleTrickyBoolean(info?.isinvisible);
        isNetworking = handleTrickyBoolean(info?.isnetworking);
      }
    }
  })();

  const handleAttendeeDetails = user => {
    if (disable) return;

    if (forceRedirect && isNetworking) {
      !_.isEqual(location.pathname, '/attendee') &&
        history.push('/attendee/' + appTabId);
      props.openMeetingModal({});
    }
    if (user?.appuserid == session.userid) {
      props.setProfilePortalToggle(userDetails, 'profile');
      history.push({
        pathname: '/myprofile'
      });
    }
    // else if (!rolePermissions?.[ALLOWED_FEATURE_KEYS.attendee]) {
    //   props.setShowCommonAccessDenied(true);
    //   hasHSPopup && props.setHSPopup({ show: true, component: '' });
    // }
    // else if (userDetailsAddOn?.isinvisible || isInvisible) {
    //   showError(
    //     userDetailsAddOn?.isinvisible
    //       ? LocalStrings?.dynamic.kInvisibleProfileDesc
    //       : LocalStrings?.dynamic.kInvisibleProfile,
    //     userDetailsAddOn?.isinvisible ? LocalStrings.dynamic.kInvisibleProfile : null
    //   );
    // }
    else if (!userDetailsAddOn?.isnetworking) {
      props.commonNetworkModal(true);
    } else if (!isNetworking && !dontCheckNW) {
      showError(LocalStrings.dynamic.kInActiveUser);
    } else {
      props.setIsVSPopup(false);
      props.setVSPopupContent('');
      props.clearAppUserNotes();
      if (checkNotes)
        props.fetchAppUserNotes(
          session,
          ALLOWED_FEATURE_KEYS.attendee,
          user?.appuserid,
          res => {}
        );
      setTimeout(() => {
        props.showAttendeeProfile(
          session,
          { show: true, value: user },
          source ?? 'profile-image'
        );
        hasHSPopup && props.setHSPopup({ show: true, component: '' });
      }, 100);
    }
  };

  const handleDropToggle = () => {
    if (!openProfile) return;

    if (showOptions && isNetworking && !isInvisible) {
      if (
        !_.isEmpty(info.videourl) ||
        (!_.isEmpty(info.profileurl) && info?.appuserid != session.userid)
      )
        setToggle(!toggle);
      else handleAttendeeDetails(info);
    } else handleAttendeeDetails(info);
  };

  const handleOpenMediaView = type => {
    setView(type);
    setOpen(true);
  };

  const imageSrc = renderProfileSrc(info, ALLOWED_FEATURE_KEYS.attendee);
  const videoUrl = info && info?.videourl ? `${CDN_URL}${info.videourl}` : '';

  const frameUrl =
    info && info?.frameurl && !hideFrame ? `${CDN_URL}${info?.frameurl}` : null;
  const videoPresent = !_.isEmpty(videoUrl) && !hideVideo;

  const profileSize = {
    height: videoPresent ? size - 6 : size,
    width: videoPresent ? size - 6 : size
  };

  React.useImperativeHandle(ref, () => ({
    handleAttendeeDetails
  }));

  if (_.isEmpty(info)) return <></>;

  return (
    <>
      <UncontrolledDropdown>
        <DropdownToggle
          className="noBackground"
          style={{ padding: 0 }}
          onClick={handleDropToggle}
        >
          {(isNetworking && !isInvisible) || dontCheckNW ? (
            <div className="d-flex justify-content-center">
              <div
                style={{
                  backgroundColor: videoPresent
                    ? 'var(--profile-video-frame)'
                    : 'transparent',
                  borderRadius: '50%',
                  padding: videoPresent ? 3 : 0
                }}
              >
                {_.isEmpty(info?.profileurl) ? (
                  <div
                    className={`profile-name-initial-${size ?? 40}${size ??
                      40} position-relative`}
                    style={{
                      ...(size ? profileSize : {}),
                      ...(!_.isEmpty(info) &&
                      isNetworking &&
                      !isInvisible &&
                      !disable
                        ? { cursor: 'pointer' }
                        : {})
                    }}
                  >
                    {/* - Frame - */}
                    {!_.isEmpty(frameUrl) ? (
                      <>
                        <div
                          className=" d-flex align-items-center justify-content-center position-absolute"
                          style={{
                            height: '100%',
                            zIndex: 3
                          }}
                        >
                          <img
                            className="attendee-user-img h-100 w-100"
                            src={frameUrl}
                            alt="Profile frame goes here"
                            style={profileSize}
                          />
                        </div>
                      </>
                    ) : (
                      ''
                    )}

                    {/* - Name initial - */}
                    <div className="d-flex justify-content-center align-items-center h-100">
                      {!_.isEmpty(info?.firstname)
                        ? info?.firstname?.[0]?.toUpperCase()
                        : ''}
                      {!_.isEmpty(info?.lastname)
                        ? info?.lastname?.[0]?.toUpperCase()
                        : ''}
                      {!_.isEmpty(info?.firstname) && !_.isEmpty(info?.lastname)
                        ? '.'
                        : ''}
                    </div>
                  </div>
                ) : (
                  <>
                    <div
                      className="attendee-user-img position-relative"
                      style={{
                        ...(size ? profileSize : {}),
                        ...(!_.isEmpty(info) &&
                        isNetworking &&
                        !isInvisible &&
                        !disable
                          ? { cursor: 'pointer' }
                          : {}),
                        backgroundColor: 'var(--white-color)'
                      }}
                    >
                      {/* - Frame - */}
                      {!_.isEmpty(frameUrl) ? (
                        <>
                          <div
                            className="attendee-user-img d-flex align-items-center justify-content-center position-absolute"
                            style={{
                              ...profileSize,
                              zIndex: 3
                            }}
                          >
                            <img
                              className="attendee-user-img h-100 w-100"
                              src={frameUrl}
                              alt="Profile frame goes here"
                              style={profileSize}
                            />
                          </div>
                        </>
                      ) : (
                        ''
                      )}

                      {/* - Profile - */}
                      <div
                        className="attendee-user-img d-flex justify-content-center position-absolute"
                        style={{
                          ...profileSize,
                          zIndex: 2
                        }}
                      >
                        {isPostComment ? (
                          <>
                            <img
                              alt={'Image'}
                              className="attendee-user-img"
                              style={size ? profileSize : {}}
                              src={imageSrc}
                              onError={e => {
                                e.target.onerror = null;
                                e.target.src = NoImgIcon;
                              }}
                            />
                          </>
                        ) : (
                          <>
                            {showVideo && info?.videourl ? (
                              <video
                                autoPlay={true}
                                loop={true}
                                muted
                                src={`${CDN_URL}${info?.videourl}`}
                                className="h-100 w-100 attendee-user-img "
                                style={{ objectFit: 'cover' }}
                              ></video>
                            ) : (
                              <LazyLoadImage
                                alt={'Image'}
                                effect="blur"
                                placeholder={
                                  <img
                                    src={NoImgIcon}
                                    className="attendee-user-img"
                                    style={size ? profileSize : {}}
                                    alt="lazy load placeholder on error"
                                  />
                                }
                                className="attendee-user-img"
                                style={size ? profileSize : {}}
                                src={imageSrc}
                                onError={e => {
                                  e.target.onerror = null;
                                  e.target.src = NoImgIcon;
                                }}
                              />
                            )}
                          </>
                        )}
                      </div>

                      {/* - Spinner - */}
                      <div
                        className="attendee-user-img d-flex align-items-center justify-content-center position-absolute"
                        style={{
                          ...profileSize,
                          zIndex: 1,
                          color: 'var(--theme-color)'
                        }}
                      >
                        <i className={`fa fa-refresh fa-spin`}></i>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          ) : (
            <div
              className={`profile-name-initial-${size ?? 40}${size ?? 40}`}
              style={{
                ...(size ? profileSize : {}),
                ...(!_.isEmpty(info) && isNetworking && !isInvisible && !disable
                  ? { cursor: 'pointer' }
                  : {})
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  height: '100%',
                  alignItems: 'center'
                }}
              >
                {LocalStrings.dynamic.kGuestUserShort}.
              </div>
            </div>
          )}
        </DropdownToggle>

        {!_.isEmpty(info) &&
        ((isNetworking && !isInvisible) || dontCheckNW) &&
        showOptions ? (
          <>
            <DropdownMenu>
              {info && info?.profileurl && (
                <DropdownItem className="text-center">
                  <span
                    className="custom-text9-normal-primary"
                    onClick={() => handleOpenMediaView('image')}
                  >
                    {LocalStrings.dynamic.kViewText}{' '}
                    {LocalStrings.dynamic.kPhotoText}
                  </span>
                </DropdownItem>
              )}
              {info && info?.videourl && (
                <DropdownItem className="text-center border-top">
                  <span
                    className="custom-text9-normal-primary"
                    onClick={() => handleOpenMediaView('video')}
                  >
                    {LocalStrings.dynamic.kViewText}{' '}
                    {LocalStrings.dynamic.kVideo}
                  </span>
                </DropdownItem>
              )}
            </DropdownMenu>
          </>
        ) : (
          ''
        )}
      </UncontrolledDropdown>

      {/* ----- Modal for profile media -----  */}
      <Lightbox
        open={open && !_.isNil(view)}
        close={() => setOpen(false)}
        plugins={[_.isEqual(view, 'video') ? Video : Zoom]}
        slides={[
          _.isEqual(view, 'video')
            ? {
                type: 'video',
                width: 1280,
                height: 720,
                poster: videoUrl,
                sources: [
                  {
                    src: videoUrl,
                    type: 'video/mp4'
                  }
                ]
              }
            : {
                src: imageSrc
              }
        ]}
        video={{ autoPlay: true }}
        render={{
          buttonPrev: () => null,
          buttonNext: () => null
        }}
        zoom={{
          scrollToZoom: true,
          maxZoomPixelRatio: 5
        }}
      />
    </>
  );
};

const mapStateToProps = state => {
  const rolePermissions = getUserRolePrivilege(
    { ...state.userDetails, ...state.userDetailsAddOn },
    state.eventTabs,
    state.userAccessByRole,
    ALLOWED_FEATURE_KEYS.attendee
  );
  const appTabId = getAppTabId(state.eventTabs, ALLOWED_FEATURE_KEYS.attendee);
  const checkNotes = getConditionalCommonFeatures(state.eventSettings)?.[
    ALLOWED_FEATURE_KEYS.notes
  ];

  return {
    session: state.session,
    userDetails: { ...state.userDetails, ...state.userDetailsAddOn },
    userDetailsAddOn: state.userDetailsAddOn,
    rolePermissions,
    appTabId,
    checkNotes
  };
};
const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      showAttendeeProfile,
      setVSPopupContent,
      setIsVSPopup,
      setHSPopup,
      commonNetworkModal,
      setProfilePortalToggle,
      setShowCommonAccessDenied,
      fetchAppUserNotes,
      clearAppUserNotes,
      openMeetingModal
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(UserProfilePic);
